import {
    AppId,
    FCX,
    getFormattedThousand,
    includesInLC,
    ProductLogo, usePlatformFeatureFlag
} from "@datassential/platform-ui-lib";
import { GrNext, GrPrevious } from "react-icons/gr";
import cn from "classnames";
import './Posts.scss';
import { SocialColor } from "@models/FoodSocial";
import {
    IconSocialsFb,
    IconSocialsInstagram,
    IconSocialsPinterest,
    IconSocialsReddit,
    IconSocialsTiktok,
    IconSocialsTumblr,
    IconSocialsYoutube,
    IconSocialsTwitter,
} from "@icons";
import { FiMessageSquare } from "react-icons/fi";
import { useMemo, useState } from "react";
import { ISocialPost, SocialPosts } from "@models/SocialPosts";

const Slide: FCX<{
    data: ISocialPost & { index: number };
    slideIndex: number;
    word: string;
    association: string;
}> = ({
    data,
    slideIndex,
    word,
    association,
}) => {
    const isMinimalFiltersFeatureEnabled = usePlatformFeatureFlag('Snap Social Listening Minimal Filters');
    const {
        sentiment,
        source,
        date,
        text,
        engagement,
        index,
        url,
    } = data;

    const textHtml = useMemo(
        () => {
            let result = text;
            const regexWord = new RegExp(`\\b(#?${word})\\b`, 'gi');
            result = result.replace(regexWord, '<b>$1</b>');

            if (association !== '') {
                const regexAssociation = new RegExp(`\\b(#?${association})\\b`, 'gi');
                result = result.replace(regexAssociation, '<b>$1</b>');
            }

            return result;
        },
        [text, word, association]
    )

    const color = useMemo(
        () => {
            if (isMinimalFiltersFeatureEnabled) {
                return SocialColor.Neutral;
            }
            else if (includesInLC(sentiment, 'positive')) {
                return SocialColor.Positive;
            }
            else if (includesInLC(sentiment, 'negative')) {
                return SocialColor.Negative;
            }
            return SocialColor.Neutral;
        },
        [sentiment, isMinimalFiltersFeatureEnabled]
    );

    const icon = useMemo(
        () => {
            if (includesInLC(source, 'facebook')) {
                return <IconSocialsFb/>;
            }
            if (includesInLC(source, 'twitter')) {
                return <IconSocialsTwitter/>;
            }
            if (includesInLC(source, 'instagram')) {
                return <IconSocialsInstagram/>;
            }
            if (includesInLC(source, 'pinterest')) {
                return <IconSocialsPinterest/>;
            }
            if (includesInLC(source, 'youtube')) {
                return <IconSocialsYoutube/>;
            }
            if (includesInLC(source, 'tiktok')) {
                return <IconSocialsTiktok/>;
            }
            if (includesInLC(source, 'tumblr')) {
                return <IconSocialsTumblr/>;
            }
            if (includesInLC(source, 'reddit')) {
                return <IconSocialsReddit/>;
            }
            return <FiMessageSquare/>;
        },
        [source]
    );

    return (
        <div
            className={cn("FoodSocialPosts__item", `slide-${slideIndex}`)}
            data-testid={`blockFoodSocialPostExamplesItem-${index}`}
        >
            <div
                className="FoodSocialPosts__item-inner"
                style={{
                    borderColor: color,
                }}
            >
                {!!url && (
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="FoodSocialPosts__item-link"
                        data-testid="linkFoodSocialPostExamplesItemUrl"
                    >
                        {url}
                    </a>
                )}
                <div className="FoodSocialPosts__item-content FoodSectionSocial__blur">
                    <div className="FoodSocialPosts__item-header">
                        <div className="FoodSocialPosts__item-header-left">
                            <div
                                className="FoodSocialPosts__item-header-icon"
                                data-testid="blockFoodSocialPostExamplesItemIcon"
                            >
                                {icon}
                            </div>
                            <div
                                className="FoodSocialPosts__item-header-text"
                                data-testid="textFoodSocialPostExamplesItemSource"
                            >
                                {source}
                            </div>
                        </div>
                        <div
                            className="FoodSocialPosts__item-header-text"
                            data-testid="textFoodSocialPostExamplesItemDate"
                        >
                            {SocialPosts.formatDate(date)}
                        </div>
                    </div>
                    <div
                        className="FoodSocialPosts__item-text"
                        data-testid="textFoodSocialPostExamplesItemText"
                        dangerouslySetInnerHTML={{ __html: textHtml }}
                    />
                </div>
                <div
                    className="FoodSocialPosts__item-footer FoodSectionSocial__blur"
                    data-testid="textFoodSocialPostExamplesItemFooter"
                >
                    engagement count: {getFormattedThousand(engagement)}
                </div>
            </div>
        </div>
    );
};

const FoodSocialPosts: FCX<{
    data: ISocialPost[];
    word: string;
    association: string;
    isLoading: boolean;
}> = ({
    data: rawData,
    word,
    association,
    isLoading,
}) => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const data = useMemo(
        () => rawData.map((item, index) => ({ ...item, index })),
        [rawData]
    );

    const visibleSlides = useMemo(
        () => {
            if (data.length === 0) return [];

            const tripleData = [...data, ...data, ...data];

            return tripleData.slice(data.length + currentSlideIndex - 1, data.length + currentSlideIndex + 4);
        },
        [data, currentSlideIndex],
    );


    return (
        <div className="FoodSocialPosts appears del-1">
            {isLoading && (
                <div className="spinner spinner-blink is-small">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            )}
            {!isLoading && data.length > 0 && (
                <>
                    <div
                        className={cn("FoodSocialPosts__nav is-prev")}
                        onClick={() => {
                            if (!isAnimating) {
                                setCurrentSlideIndex(currentSlideIndex === 0 ? data.length - 1 : currentSlideIndex - 1);
                                setIsAnimating(true);
                                setTimeout(() => setIsAnimating(false), 550);
                            }
                        }}
                        data-testid="buttonFoodSocialPostExamplesNavPrev"
                    >
                        <GrPrevious/>
                    </div>
                    <div
                        className={cn("FoodSocialPosts__nav is-next")}
                        onClick={() => {
                            if (!isAnimating) {
                                setCurrentSlideIndex(currentSlideIndex === data.length - 1 ? 0 : currentSlideIndex + 1);
                                setIsAnimating(true);
                                setTimeout(() => setIsAnimating(false), 550);
                            }
                        }}
                        data-testid="buttonFoodSocialPostExamplesNavNext"
                    >
                        <GrNext/>
                    </div>
                    <div className={cn("FoodSocialPosts__inner", isAnimating && 'is-ov-hid')}>
                        {data.length > 3 ? (
                            visibleSlides.map((item, index) => (
                                <Slide
                                    key={item.id}
                                    data={item}
                                    slideIndex={index}
                                    word={word}
                                    association={association}
                                />
                            ))
                        ) : (
                            data.map((item, index) => (
                                <Slide
                                    key={item.id}
                                    data={item}
                                    slideIndex={index + 1}
                                    word={word}
                                    association={association}
                                />
                            ))
                        )}
                    </div>
                </>
            )}
            {!isLoading && data.length === 0 && (
                <div className="FoodSocialPosts__no-results">
                    No social posts found.
                </div>
            )}
        </div>
    );
};

export default FoodSocialPosts;